import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MailX, CheckCircle2, Sprout } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicUnsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setError('This link is invalid.'); setLoading(false); return; }
    axios.get(`${API}/public/unsubscribe/${token}`)
      .then((res) => {
        setEmail(res.data.email || '');
        if (res.data.already) setDone(true);
      })
      .catch(() => setError('This unsubscribe link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const unsubscribe = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/unsubscribe/${token}`);
      setDone(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8 text-center" data-testid="unsubscribe-page">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-6">
          <Sprout className="w-6 h-6 text-white" />
        </div>

        {loading ? (
          <Loader2 className="w-7 h-7 animate-spin text-slate-400 mx-auto" />
        ) : error ? (
          <>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Link not valid</h1>
            <p className="text-sm text-slate-500">{error}</p>
          </>
        ) : done ? (
          <div data-testid="unsubscribe-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">You're unsubscribed</h1>
            <p className="text-sm text-slate-500">
              {email && <span className="font-medium text-slate-700">{email}</span>} won't receive any more marketing emails from us. Sorry to see you go!
            </p>
          </div>
        ) : (
          <>
            <MailX className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Unsubscribe?</h1>
            <p className="text-sm text-slate-500 mb-6">
              {email && <span className="font-medium text-slate-700">{email}</span>} will stop receiving marketing emails from us.
            </p>
            <button
              onClick={unsubscribe}
              disabled={submitting}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg px-5 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              data-testid="confirm-unsubscribe-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Unsubscribing…</> : 'Yes, unsubscribe me'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicUnsubscribe;
